import { useEffect, useId, useState } from "react";
import type { AnalyticsContent, AnalyticsDashboard as AnalyticsData, AnalyticsFeature, AnalyticsTrend } from "../shared/analytics";
import "./analytics-dashboard.css";

export type AnalyticsDateRange = { from: string; to: string };

export function analyticsToday(now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(now);
  const part = (kind: Intl.DateTimeFormatPartTypes): string => parts.find((item) => item.type === kind)!.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function analyticsPresetRange(days: number, today = analyticsToday()): AnalyticsDateRange {
  const start = new Date(`${today}T00:00:00Z`);
  start.setUTCDate(start.getUTCDate() - days + 1);
  return { from: start.toISOString().slice(0, 10), to: today };
}

export function analyticsRangeError(range: AnalyticsDateRange, today = analyticsToday()): string | null {
  const valid = (value: string): boolean => {
    if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
    const parsed = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  };
  if (!valid(range.from) || !valid(range.to)) return "请选择有效的开始和结束日期。";
  if (range.from > range.to) return "开始日期不能晚于结束日期。";
  if (range.to > today) return "结束日期不能晚于今天。";
  if ((Date.parse(range.to) - Date.parse(range.from)) / 86_400_000 >= 90) return "一次最多查询 90 天。";
  return null;
}

export function analyticsCsvCell(value: string | number | null): string {
  let text = value === null ? "" : String(value);
  // Quoting alone does not stop spreadsheet formula evaluation. Protect both
  // plain formula prefixes and prefixes hidden behind leading whitespace.
  if (/^[\s\uFEFF]*[=+@-]|^[\t\r\n]/u.test(text)) text = `'${text}`;
  return `"${text.replaceAll('"', '""')}"`;
}

export function analyticsCsvRows(rows: (string | number | null)[][]): string {
  return `\uFEFF${rows.map((row) => row.map(analyticsCsvCell).join(",")).join("\r\n")}\r\n`;
}

function duration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)} 秒`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} 分钟`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours.toLocaleString("zh-CN")} 小时${minutes ? ` ${minutes} 分` : ""}`;
}

function timestamp(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value.includes("T") ? value : `${value.replace(" ", "T")}Z`);
  if (!Number.isFinite(date.getTime())) return "—";
  return new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(date);
}

type TrendPoint = { day: string; value: number };

function TrendChart({ points, label }: { points: TrendPoint[]; label: string }): React.JSX.Element {
  const titleId = useId();
  const max = Math.max(1, ...points.map((point) => point.value));
  const step = 820 / Math.max(1, points.length);
  return (
    <svg viewBox="0 0 900 250" role="img" aria-labelledby={titleId}>
      <title id={titleId}>{label}每日趋势，下方提供完整数据表</title>
      {[0, 0.5, 1].map((scale) => (
        <g key={scale}>
          <line className="analytics-chart-grid" x1="58" x2="878" y1={210 - scale * 180} y2={210 - scale * 180} />
          <text className="analytics-chart-label" x="48" y={214 - scale * 180} textAnchor="end">{Math.round(max * scale).toLocaleString("zh-CN")}</text>
        </g>
      ))}
      {points.map((point, index) => <rect key={point.day} className="analytics-chart-bar" x={58 + index * step + step * 0.15} y={210 - point.value / max * 180} width={Math.max(1, step * 0.7)} height={point.value / max * 180}><title>{point.day}：{point.value}</title></rect>)}
      {points.length > 0 && <>
        <text className="analytics-chart-label" x="58" y="236">{points[0].day.slice(5)}</text>
        <text className="analytics-chart-label" x="878" y="236" textAnchor="end">{points.at(-1)!.day.slice(5)}</text>
      </>}
    </svg>
  );
}

const featureLabels: Record<AnalyticsFeature, string> = {
  nav_home: "首页", nav_periodicals: "期刊", nav_podcasts: "播客", search_open: "打开主题检索", search_submit: "提交检索",
  toc_open: "打开目录", toc_select: "选择文章", language_zh: "切换中文", language_en: "切换原文", reading_settings: "阅读设置",
  theme_change: "调整主题", font_change: "调整字号", immersive_toggle: "切换沉浸阅读", discussion_open: "打开讨论", comment_submit: "提交评论",
  ai_open: "打开 AI 研读", ai_submit: "提交 AI 问题", ai_citation: "打开 AI 引用", podcast_play: "播放播客", podcast_pause: "暂停播客",
  podcast_speed: "调整播放速度", podcast_chapter: "切换播客章节", podcast_mode: "切换播客阅读方式", publication_filter: "筛选期刊",
  podcast_filter: "筛选播客", about_open: "关于", logout: "退出登录", admin_open: "打开统计后台",
};
const roles = { reader: "读者", editor: "编辑", admin: "管理员" };
const contentLabels = { article: "文章", issue: "期刊", podcast: "播客" };
const metricLabels = {
  logins: "登录次数", loginUsers: "登录人数", activeUsers: "活跃用户", readers: "有效阅读人数", sessions: "访问会话", pageViews: "页面浏览 PV",
  opens: "打开记录", engagedReads: "有效阅读", completedReads: "读完记录", audioStarts: "播放记录", audioCompletions: "听完记录", readSeconds: "前台阅读秒数",
};

export async function loadAnalytics(range: AnalyticsDateRange, signal?: AbortSignal): Promise<AnalyticsData> {
  const query = new URLSearchParams({ start: range.from, end: range.to });
  const response = await fetch(`/api/admin/analytics?${query.toString()}`, { credentials: "same-origin", signal });
  let payload: unknown;
  try { payload = await response.json(); } catch { throw new Error("统计服务暂时无法响应，请稍后重试。"); }
  if (!response.ok) {
    if (response.status === 401) throw new Error("登录已过期，请重新登录后查看。");
    if (response.status === 403) throw new Error("仅管理员可以查看统计后台。");
    throw new Error("统计加载失败，请稍后重试。");
  }
  if (!payload || typeof payload !== "object" || !("ok" in payload) || payload.ok !== true || !("summary" in payload) || !payload.summary || typeof payload.summary !== "object" || !("trend" in payload) || !Array.isArray(payload.trend)) {
    throw new Error("统计数据格式异常，请稍后重试。");
  }
  const summary = payload.summary;
  if ([...Object.keys(metricLabels), "totalUsers", "newUsers"].some((key) => !(key in summary) || typeof (summary as Record<string, unknown>)[key] !== "number") ||
      ["popularContent", "features", "users", "notes"].some((key) => !(key in payload) || !Array.isArray((payload as Record<string, unknown>)[key]))) {
    throw new Error("统计数据格式异常，请稍后重试。");
  }
  return payload as AnalyticsData;
}

export function buildAnalyticsCsv(data: AnalyticsData): string {
  const rows: (string | number | null)[][] = [
    ["FOLIO 阅读统计"], ["开始日期", data.range.start, "结束日期", data.range.end, "时区", data.range.timeZone],
    ["生成时间", data.generatedAt], ["新埋点采集起始", data.collectionStartedAt],
    ["口径", "历史打开、有效阅读、读完按用户 × 内容 × 日去重；PV、功能点击、前台时长仅来自新埋点，不是历史全量。"],
    ...data.notes.map((note) => ["说明", note]), [], ["汇总指标", "值"],
    ...Object.entries(metricLabels).map(([key, label]) => [label, data.summary[key as keyof typeof metricLabels]]),
    ["总用户数", data.summary.totalUsers], ["新增用户数", data.summary.newUsers],
    [], ["每日趋势", ...Object.values(metricLabels)],
    ...data.trend.map((point) => [point.date, ...Object.keys(metricLabels).map((key) => point[key as keyof typeof metricLabels])]),
    [], ["热门内容类型", "标题", "出版物", "内容ID", "互动人数", "打开记录", "有效阅读", "读完记录", "播放记录", "听完记录", "前台阅读秒数"],
    ...data.popularContent.map((item) => [contentLabels[item.contentType], item.title, item.publication, item.contentId, item.readers, item.opens, item.engagedReads, item.completedReads, item.audioStarts, item.audioCompletions, item.readSeconds]),
    [], ["功能", "点击次数", "使用人数"],
    ...data.features.map((item) => [featureLabels[item.feature] ?? item.feature, item.clicks, item.users]),
    [], ["用户姓名", "角色", "用户ID", "登录次数", "最近登录", "最近活跃", "活跃天数", "页面浏览PV", "打开记录", "有效阅读", "读完记录", "播放记录", "前台阅读秒数"],
    ...data.users.map((user) => [user.name, roles[user.role], user.id, user.logins, user.lastLoginAt, user.lastActiveAt, user.activeDays, user.pageViews, user.opens, user.engagedReads, user.completedReads, user.audioStarts, user.readSeconds]),
  ];
  return analyticsCsvRows(rows);
}

function ContentRanking({ title, items, podcast = false }: { title: string; items: AnalyticsContent[]; podcast?: boolean }): React.JSX.Element {
  return <section className="analytics-section">
    <div className="analytics-section-head"><h2>{title}</h2><p>按有效阅读优先排序 · 去重记录</p></div>
    {items.length === 0 ? <p className="analytics-empty">所选日期内暂无{title.replace("热门", "")}记录。</p> :
      <div className="analytics-table-scroll" role="region" aria-label={title} tabIndex={0}>
        <table className="analytics-table"><thead><tr><th scope="col">内容</th><th scope="col" className="analytics-number">互动人数</th><th scope="col" className="analytics-number">有效阅读</th><th scope="col" className="analytics-number">{podcast ? "播放 / 听完" : "读完"}</th><th scope="col" className="analytics-number">前台时长</th></tr></thead>
          <tbody>{items.map((item) => <tr key={item.contentId}><td className="analytics-content-title">{item.title}<small>{item.publication}</small></td><td className="analytics-number">{item.readers}</td><td className="analytics-number">{item.engagedReads}</td><td className="analytics-number">{podcast ? `${item.audioStarts} / ${item.audioCompletions}` : item.completedReads}</td><td className="analytics-number">{duration(item.readSeconds)}</td></tr>)}</tbody>
        </table>
      </div>}
  </section>;
}

type TrendMetric = "activeUsers" | "logins" | "engagedReads" | "readSeconds" | "pageViews";
const trendLabels: Record<TrendMetric, string> = { activeUsers: "活跃用户", logins: "登录次数", engagedReads: "有效阅读", readSeconds: "前台阅读时长（分钟）", pageViews: "页面浏览 PV" };

export function AnalyticsReport({ data }: { data: AnalyticsData }): React.JSX.Element {
  const [trendMetric, setTrendMetric] = useState<TrendMetric>("activeUsers");
  const s = data.summary;
  const count = (value: number): string => value.toLocaleString("zh-CN");
  const metrics = [
    ["登录次数", count(s.logins), `${count(s.loginUsers)} 位用户登录`],
    ["活跃用户", count(s.activeUsers), `总用户 ${count(s.totalUsers)} · 新增 ${count(s.newUsers)}`],
    ["阅读人数", count(s.readers), `${count(s.opens)} 条打开记录 · 有效阅读用户去重`],
    ["有效阅读", count(s.engagedReads), "用户 × 内容 × 日去重"],
    ["读完记录", count(s.completedReads), "用户 × 内容 × 日去重"],
    ["前台阅读时长", duration(s.readSeconds), "仅统计新埋点的有效前台时间"],
    ["页面浏览 PV", count(s.pageViews), `${count(s.sessions)} 次访问会话 · 新埋点`],
    ["播客播放", count(s.audioStarts), `${count(s.audioCompletions)} 条听完记录 · 去重`],
  ];
  const empty = s.activeUsers === 0 && s.logins === 0 && s.opens === 0 && s.pageViews === 0 && s.readSeconds === 0;
  const ranked = (type: AnalyticsContent["contentType"]): AnalyticsContent[] => data.popularContent.filter((item) => item.contentType === type).toSorted((a, b) => b.engagedReads - a.engagedReads || b.readers - a.readers || b.opens - a.opens);
  return <>
    <p className="analytics-range">{data.range.start} — {data.range.end} · 共 {data.range.days} 天 · 北京时间</p>
    {empty && <div className="analytics-status"><h2>这段时间还没有活动记录</h2><p>试试扩大日期范围。新采集指标不会补算历史数据。</p></div>}
    <dl className="analytics-metrics">{metrics.map(([label, value, note]) => <div className="analytics-metric" key={label}><dt>{label}</dt><dd>{value}</dd><small>{note}</small></div>)}</dl>
    <section className="analytics-section analytics-trend">
      <div className="analytics-section-head"><div><h2>每日趋势</h2><p>以北京时间自然日统计</p></div><label><span className="analytics-sr-only">趋势指标</span><select value={trendMetric} onChange={(event) => setTrendMetric(event.target.value as TrendMetric)}>{Object.entries(trendLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label></div>
      <TrendChart label={trendLabels[trendMetric]} points={data.trend.map((point) => ({ day: point.date, value: trendMetric === "readSeconds" ? Math.round(point.readSeconds / 60) : point[trendMetric] }))} />
      <details><summary>查看每日明细表</summary><div className="analytics-table-scroll" role="region" aria-label="每日统计明细" tabIndex={0}><table className="analytics-table"><thead><tr><th scope="col">日期</th><th scope="col" className="analytics-number">活跃用户</th><th scope="col" className="analytics-number">登录</th><th scope="col" className="analytics-number">有效阅读</th><th scope="col" className="analytics-number">读完</th><th scope="col" className="analytics-number">PV</th><th scope="col" className="analytics-number">前台时长</th></tr></thead><tbody>{data.trend.map((point: AnalyticsTrend) => <tr key={point.date}><td>{point.date}</td>{[point.activeUsers, point.logins, point.engagedReads, point.completedReads, point.pageViews].map((value, index) => <td key={index} className="analytics-number">{count(value)}</td>)}<td className="analytics-number">{duration(point.readSeconds)}</td></tr>)}</tbody></table></div></details>
    </section>
    <ContentRanking title="热门文章" items={ranked("article")} />
    <div className="analytics-columns"><ContentRanking title="热门期刊" items={ranked("issue")} /><ContentRanking title="热门播客" items={ranked("podcast")} podcast /></div>
    <section className="analytics-section"><div className="analytics-section-head"><h2>功能使用</h2><p>实际点击次数 · 仅新埋点</p></div>
      {data.features.length === 0 ? <p className="analytics-empty">所选日期内暂无功能点击记录。</p> : <div className="analytics-table-scroll" role="region" aria-label="功能使用排行" tabIndex={0}><table className="analytics-table"><thead><tr><th scope="col">功能</th><th scope="col" className="analytics-number">点击</th><th scope="col" className="analytics-number">使用人数</th></tr></thead><tbody>{data.features.toSorted((a, b) => b.clicks - a.clicks).map((item) => <tr key={item.feature}><td>{featureLabels[item.feature] ?? item.feature}</td><td className="analytics-number">{count(item.clicks)}</td><td className="analytics-number">{count(item.users)}</td></tr>)}</tbody></table></div>}
    </section>
    <section className="analytics-section"><div className="analytics-section-head"><h2>用户活跃</h2><p>{data.users.length} 位用户 · 次数和时长按所选日期，最近活跃为历史最新</p></div>
      {data.users.length === 0 ? <p className="analytics-empty">暂无用户记录。</p> : <div className="analytics-table-scroll" role="region" aria-label="用户活跃明细" tabIndex={0}><table className="analytics-table"><thead><tr><th scope="col">姓名</th><th scope="col">角色</th><th scope="col" className="analytics-number">登录次数</th><th scope="col" className="analytics-number">有效阅读</th><th scope="col" className="analytics-number">前台时长</th><th scope="col">最近登录</th><th scope="col">最近活跃</th></tr></thead><tbody>{data.users.map((user) => <tr key={user.id}><td className="analytics-content-title">{user.name || "未命名用户"}</td><td>{roles[user.role]}</td><td className="analytics-number">{count(user.logins)}</td><td className="analytics-number">{count(user.engagedReads)}</td><td className="analytics-number">{duration(user.readSeconds)}</td><td>{timestamp(user.lastLoginAt)}</td><td>{timestamp(user.lastActiveAt)}</td></tr>)}</tbody></table></div>}
    </section>
    <aside className="analytics-methodology"><h2>统计口径</h2><p>历史打开、有效阅读和读完记录按「用户 × 内容 × 日」去重，同一天重复阅读同一内容只计一条；这些记录不是页面浏览 PV。</p><p>页面浏览 PV、功能点击与前台阅读时长从{data.collectionStartedAt ? ` ${timestamp(data.collectionStartedAt)}（北京时间）` : "新埋点启用后"}开始采集{data.collectionStartedAt ? "。" : "，当前尚无采集记录。"}此前没有对应数据，不代表此前无人使用。</p><p>前台阅读时长仅累计页面可见且正在阅读的时间，不等于页面打开时长或播客播放时长。</p>{data.notes.map((note, index) => <p key={index}>{note}</p>)}</aside>
  </>;
}

export function AnalyticsDashboard({ onClose }: { onClose: () => void }): React.JSX.Element {
  const [preset, setPreset] = useState<number | "custom">(30);
  const [range, setRange] = useState<AnalyticsDateRange>(() => analyticsPresetRange(30));
  const [custom, setCustom] = useState<AnalyticsDateRange>(range);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(null); setData(null);
    void loadAnalytics(range, controller.signal).then((result) => {
      if (!controller.signal.aborted) setData(result);
    }).catch((reason: unknown) => {
      if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : "统计加载失败，请稍后重试。");
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [range, reload]);
  function choosePreset(value: number | "custom"): void {
    setPreset(value); setFieldError(null);
    if (value !== "custom") { const next = analyticsPresetRange(value); setRange(next); setCustom(next); }
  }
  function applyCustom(event: React.FormEvent): void {
    event.preventDefault();
    const invalid = analyticsRangeError(custom); setFieldError(invalid);
    if (!invalid) setRange({ ...custom });
  }
  function exportCsv(): void {
    if (!data) return;
    try {
      const url = URL.createObjectURL(new Blob([buildAnalyticsCsv(data)], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a"); link.href = url; link.download = `FOLIO-统计-${data.range.start}-${data.range.end}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { setFieldError("导出失败，请重试或检查浏览器下载设置。"); }
  }
  return <main className="analytics-dashboard">
    <header className="analytics-header"><div><p className="analytics-kicker">FOLIO / READING INSIGHTS</p><h1>阅读统计</h1><p>了解内容如何被阅读，以及读者如何使用 FOLIO。</p></div><button className="analytics-back" onClick={onClose}>返回阅读</button></header>
    <div className="analytics-filters"><div className="analytics-presets" role="group" aria-label="统计日期范围">{([7, 30, 90, "custom"] as const).map((value) => <button key={value} aria-pressed={preset === value} onClick={() => choosePreset(value)}>{value === "custom" ? "自定义" : `近 ${value} 天`}</button>)}</div>
      {preset === "custom" && <form className="analytics-custom" onSubmit={applyCustom}><label>开始日期<input type="date" required value={custom.from} max={custom.to || analyticsToday()} onChange={(event) => setCustom({ ...custom, from: event.target.value })} /></label><label>结束日期<input type="date" required value={custom.to} min={custom.from} max={analyticsToday()} onChange={(event) => setCustom({ ...custom, to: event.target.value })} /></label><button className="analytics-apply" type="submit">查询</button></form>}
      <button className="analytics-export" onClick={exportCsv} disabled={loading || !data}>导出 CSV</button>
      {fieldError && <p className="analytics-field-error" role="alert">{fieldError}</p>}
    </div>
    <div aria-busy={loading}>
      {loading ? <div className="analytics-status" role="status">正在整理阅读记录…</div> : error ? <div className="analytics-status analytics-error" role="alert"><h2>暂时无法加载统计</h2><p>{error}</p><button className="analytics-retry" onClick={() => setReload((value) => value + 1)}>重新加载</button></div> : data ? <AnalyticsReport data={data} /> : null}
    </div>
  </main>;
}
